import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSecurity } from '../context/SecurityContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { FormInput, PasswordInput, EmailInput } from '../components/ui/form-input';
import { useToast } from '../hooks/use-toast';
import { Key, ArrowLeft, AlertCircle, X, Store } from 'lucide-react';
import { getFriendlyError } from '../utils/friendlyErrors';

// Google Icon SVG
const GoogleIcon = () => (
  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, user, sessionExpired } = useAuth();
  const { setTokensAfterLogin } = useSecurity();
  const { branding } = useTheme();
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassKey, setShowPassKey] = useState(false);
  const [passKey, setPassKey] = useState('');
  const [loadingPassKey, setLoadingPassKey] = useState(false);
  const [loadingLogin, setLoadingLogin] = useState(false);
  const [gstReminderToast, setGstReminderToast] = useState(null);
  const [loginError, setLoginError] = useState(null);
  const [shakeFields, setShakeFields] = useState({ email: false, password: false });

  // iter-230ab — Auto-redirect to /dashboard ONLY when this is a true,
  // post-login user state (not a transient storage-event restore racing
  // with a soft session-expired flow). `sessionExpired` is set by the
  // AuthContext on a 401-cascade for vendors; if it's true we DO NOT
  // want to navigate, because the user is on this page intentionally
  // to re-enter their password. Without this guard, the previous
  // single-condition redirect bounced vendors back to /dashboard the
  // instant any storage event briefly rehydrated `user`, producing the
  // dashboard ↔ login flicker captured in the production video.
  useEffect(() => {
    if (user && user.role === 'vendor' && !sessionExpired) {
      navigate('/dashboard');
    }
  }, [user, navigate, sessionExpired]);

  // Handle OAuth errors from redirect
  useEffect(() => {
    const error = searchParams.get('error');
    if (error) {
      const errorMessages = {
        'oauth_denied': 'Google sign-in was cancelled',
        'missing_params': 'Authentication parameters missing',
        'invalid_state': 'Invalid authentication session',
        'state_expired': 'Authentication session expired. Please try again.',
        'token_exchange_failed': 'Failed to complete authentication',
        'userinfo_failed': 'Failed to get user information',
        'oauth_error': 'An error occurred during authentication',
        'no_email': 'No email address found in Google account',
        'account_inactive': 'Your account is not active. Please contact support.',
        'auth_failed': 'Authentication failed. Please try again.'
      };
      
      toast({
        title: 'Sign-in Error',
        description: errorMessages[error] || 'An error occurred during sign-in',
        variant: 'destructive'
      });
    }
  }, [searchParams, toast]);

  // Google OAuth handler for Vendor.
  // Backend derives the post-auth redirect from the request's Origin header,
  // so we don't need to pass a redirect_url query param. This works correctly
  // across preview / production / custom domains because the browser sends
  // its actual origin via the redirect's Referer/Origin header.
  const handleGoogleSignIn = () => {
    window.location.href = `${window.location.origin}/api/auth/google?role=vendor`;
  };

  const handleGSTReminderCheck = async (userId, accessToken) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/vendors/gst-reminder-status`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        if (data.needs_reminder) {
          const incrementResponse = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/vendors/increment-gst-reminder`,
            {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
              }
            }
          );

          if (incrementResponse.ok) {
            const reminderData = await incrementResponse.json();
            
            if (reminderData.show_reminder) {
              setGstReminderToast({
                message: reminderData.message,
                count: reminderData.count,
                showDismiss: reminderData.show_dismiss_option
              });
            }
          }
        }
      }
    } catch (error) {
      console.error('Failed to check GST reminder:', error);
    }
  };

  const handleDismissReminder = async () => {
    try {
      const token = sessionStorage.getItem('token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/vendors/dismiss-gst-reminder`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      setGstReminderToast(null);
      toast({
        title: 'Reminder Dismissed',
        description: 'You won&apos;t see this reminder again.'
      });
    } catch (error) {
      console.error('Failed to dismiss reminder:', error);
    }
  };

  const triggerShake = (field) => {
    setShakeFields(prev => ({ ...prev, [field]: true }));
    setTimeout(() => {
      setShakeFields(prev => ({ ...prev, [field]: false }));
    }, 500);
  };

  const handleLogin = async () => {
    // Client-side validation with shake animation
    if (!email) {
      setLoginError({ type: 'email', message: 'Please enter your email address' });
      triggerShake('email');
      return;
    }
    
    if (!password) {
      setLoginError({ type: 'password', message: 'Please enter your password' });
      triggerShake('password');
      return;
    }

    setLoginError(null);
    setLoadingLogin(true);
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${BACKEND_URL}/api/auth/login`);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.withCredentials = true; // Important: Include cookies for refresh token
    
    xhr.onload = async () => {
      setLoadingLogin(false);
      
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText);
          
          // Store refresh_token in BOTH sessionStorage AND localStorage —
          // localStorage acts as cross-tab passport so newly opened tabs can
          // hydrate their per-tab sessionStorage. The refresh token is also
          // set as an HttpOnly cookie by the backend (defence in depth).
          if (data.refresh_token) {
            sessionStorage.setItem('refresh_token', data.refresh_token);
            try { localStorage.setItem('refresh_token', data.refresh_token); } catch (_) { /* quota */ }
          }
          
          if (data.must_change_password) {
            sessionStorage.setItem('temp_token', data.access_token);
            sessionStorage.setItem('temp_user', JSON.stringify(data.user));
            
            toast({
              title: '🔐 Password Change Required',
              description: 'Please change your temporary password to continue.',
            });
            
            navigate('/change-password?required=true');
            return;
          }
          
          // Store onboarding redirect for PrivateRoute enforcement
          const isPending = ['pending', 'PENDING_ONBOARDING', 'pending_onboarding'].includes(data.user.status);
          if (isPending && !data.user.onboarding_completed && data.redirect_path) {
            localStorage.setItem('onboarding_redirect', data.redirect_path);
          } else {
            localStorage.removeItem('onboarding_redirect');
          }
          
          // Await login to ensure state is set before navigation
          await login(data.user, data.access_token);
          // Activate SecurityContext's proactive token refresh & idle tracking
          setTokensAfterLogin(
            data.access_token,
            data.expires_in_seconds || 3600,
            data.security_settings
          );
          
          toast({
            title: '✅ Success',
            description: `Welcome back, ${data.user.name}!`,
          });
          
          await handleGSTReminderCheck(data.user.id, data.access_token);
          
          // Check if there's a redirect URL stored (from being kicked out during onboarding)
          const storedRedirect = sessionStorage.getItem('redirectAfterLogin');
          if (storedRedirect) {
            sessionStorage.removeItem('redirectAfterLogin');
            navigate(storedRedirect);
            return;
          }
          
          // Trust backend's redirect_path decision (Dashboard Anchor + Smart Resume)
          // Backend calculates the correct path based on onboarding lifecycle
          // SAFETY: Validate redirect_path to prevent blank pages from invalid routes
          const validRedirectPath = data.redirect_path && 
            typeof data.redirect_path === 'string' && 
            data.redirect_path.startsWith('/') && 
            data.redirect_path.trim().length > 1;
          
          if (validRedirectPath) {
            // Additional check: ensure the path doesn't lead to an empty route
            const invalidPaths = ['/undefined', '/null', '//', '/login', '/admin/login'];
            if (!invalidPaths.includes(data.redirect_path.toLowerCase())) {
              navigate(data.redirect_path);
            } else {
              // Fallback to dashboard for invalid paths
              navigate('/dashboard');
            }
          } else {
            // Fallback to dashboard if redirect_path not provided or invalid
            navigate('/dashboard');
          }
        } catch (parseError) {
          console.error('Error parsing success response:', parseError);
          toast({
            title: 'Unable to log in',
            description: 'An unexpected error occurred. Please refresh the page and try again.',
            variant: 'destructive'
          });
        }
      } else {
        try {
          const errorData = JSON.parse(xhr.responseText);
          const errorCode = errorData.detail || 'Login failed';
          
          if (errorCode === 'email_not_found') {
            setLoginError({
              type: 'email',
              message: 'No account found with this email. Please check the email or sign up for a new account.'
            });
            triggerShake('email');
          } else if (errorCode === 'incorrect_password') {
            setLoginError({
              type: 'password',
              message: 'Incorrect password. Please try again or use "Forgot Password" to reset it.'
            });
            triggerShake('password');
          } else if (errorCode.toLowerCase().includes('pending') || errorCode.toLowerCase().includes('inactive') || errorCode.toLowerCase().includes('approval')) {
            setLoginError({
              type: 'account',
              message: 'Your account is pending approval. You will be notified once it is activated.'
            });
          } else {
            toast({
              title: 'Unable to log in',
              description: getFriendlyError(errorCode, errorCode),
              variant: 'destructive'
            });
          }
        } catch (parseError) {
          toast({
            title: 'Unable to log in',
            description: 'Something went wrong. Please refresh the page and try again.',
            variant: 'destructive'
          });
        }
      }
    };
    
    xhr.onerror = () => {
      setLoadingLogin(false);
      toast({
        title: 'Unable to log in',
        description: getFriendlyError('Failed to fetch', 'Unable to reach the server. Please check your internet connection and try again.'),
        variant: 'destructive'
      });
    };
    
    xhr.send(JSON.stringify({ email, password, role: 'vendor' }));
  };

  const handlePassKeyLogin = async (e) => {
    e.preventDefault();
    
    if (!passKey || passKey.trim() === '') {
      toast({
        title: '❌ Error',
        description: 'Please enter a pass key',
        variant: 'destructive'
      });
      return;
    }

    setLoadingPassKey(true);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

      const response = await fetch(`${BACKEND_URL}/api/team/pass-keys/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          pass_key: passKey.trim().toUpperCase()
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Invalid pass key');
      }
      
      const tempUser = {
        id: 'temp_' + Date.now(),
        name: `Temporary: ${data.role_name}`,
        email: 'temporary@access',
        role: 'vendor',  // Always set to vendor for passkey access
        is_temporary: true,
        permissions: data.permissions,
        passkey_role: data.role_name,
        expires_at: data.expires_at
      };
      
      await login(tempUser, data.access_token);
      // Activate SecurityContext's proactive token refresh & idle tracking
      setTokensAfterLogin(data.access_token, data.expires_in_seconds || 3600);
      
      toast({
        title: '✅ Temporary Access Granted',
        description: `You have ${data.role_name} access until ${new Date(data.expires_at).toLocaleString()}`,
      });
      
      navigate('/dashboard');
    } catch (error) {
      toast({
        title: 'Access Denied',
        description: getFriendlyError(error, 'Invalid or expired pass key. Please check and try again.'),
        variant: 'destructive'
      });
    } finally {
      setLoadingPassKey(false);
    }
  };

  // Pass Key Login Form
  const renderPassKeyForm = () => (
    <div className="space-y-4">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setShowPassKey(false)}
        className="mb-4"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Login
      </Button>

      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-3">
          <Key className="h-8 w-8 text-blue-600" />
        </div>
        <h3 className="text-xl font-semibold mb-2">Temporary Access</h3>
        <p className="text-sm text-gray-600">
          Enter your temporary pass key to access the portal
        </p>
      </div>

      <form onSubmit={handlePassKeyLogin} className="space-y-4">
        <FormInput
          id="passkey"
          label="Pass Key"
          type="text"
          placeholder="TPK-XXXX-XXXX"
          value={passKey}
          onChange={(e) => setPassKey(e.target.value.toUpperCase())}
          className="text-center"
          inputClassName="text-lg font-mono tracking-wider text-center"
          maxLength={13}
          required
          hint="Format: TPK-XXXX-XXXX"
        />

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
          <p className="text-xs text-yellow-800">
            <strong>Note:</strong> Pass keys allow multiple logins within the valid timeframe and will expire after the specified duration.
            Contact the administrator if you need access.
          </p>
        </div>

        <Button 
          type="submit" 
          className="w-full" 
          size="lg"
          disabled={loadingPassKey}
        >
          {loadingPassKey ? 'Verifying...' : 'Access Portal'}
        </Button>
      </form>
    </div>
  );

  // Main Vendor Login Form
  const renderLoginForm = () => (
    <>
      {/* Google Sign-In Button */}
      <Button
        type="button"
        variant="outline"
        className="w-full mb-4 h-11 border-gray-300 hover:bg-gray-50"
        onClick={handleGoogleSignIn}
        data-testid="vendor-google-signin"
      >
        <GoogleIcon />
        Continue with Google
      </Button>
      
      <div className="relative my-4">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-white px-2 text-gray-500">Or continue with email</span>
        </div>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); handleLogin(); }} className="space-y-4">
        {/* Account Error Alert */}
        {loginError && loginError.type === 'account' && (
          <div className="flex items-start gap-2 p-3 rounded-lg border bg-amber-50 border-amber-200 text-amber-800 animate-fadeIn">
            <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium">Account Issue</p>
              <p className="text-sm mt-0.5">{loginError.message}</p>
            </div>
            <button 
              type="button"
              onClick={() => setLoginError(null)}
              className="flex-shrink-0"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
        
        {/* Email Input */}
        <EmailInput
          id="vendor-email"
          label="Email"
          placeholder="vendor@marketplace.com"
          value={email}
          onChange={(e) => { setEmail(e.target.value); setLoginError(null); }}
          error={loginError?.type === 'email' ? loginError.message : undefined}
          shake={shakeFields.email}
          required
          showRequiredHint={false}
        />
        
        {/* Password Input */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="vendor-password" className="text-sm font-medium">
              Password <span className="text-red-500">*</span>
            </Label>
            <button
              type="button"
              onClick={() => navigate('/forgot-password')}
              className="text-sm text-blue-600 hover:underline"
            >
              Forgot Password?
            </button>
          </div>
          <PasswordInput
            id="vendor-password"
            label=""
            placeholder="Enter password"
            value={password}
            onChange={(e) => { setPassword(e.target.value); setLoginError(null); }}
            error={loginError?.type === 'password' ? loginError.message : undefined}
            shake={shakeFields.password}
            required
            showRequiredHint={false}
            hint=""
          />
        </div>
        
        <Button 
          type="submit" 
          className="w-full bg-blue-600 hover:bg-blue-700 text-white" 
          size="lg" 
          disabled={loadingLogin}
          data-testid="vendor-login-submit"
        >
          {loadingLogin ? 'Signing in...' : 'Login to Vendor Portal'}
        </Button>
      </form>

      {/* Temporary Access Link */}
      <div className="mt-6">
        <Button
          variant="outline"
          className="w-full"
          onClick={() => setShowPassKey(true)}
          data-testid="vendor-passkey-btn"
        >
          <Key className="h-4 w-4 mr-2" />
          Temporary Access / Enter Pass Key
        </Button>
      </div>

      {/* Sign Up Link */}
      <div className="mt-6 text-center border-t pt-6">
        <p className="text-sm text-gray-600">
          Don&apos;t have an account?{' '}
          <Link 
            to="/register" 
            className="font-semibold text-blue-600 hover:text-blue-700 hover:underline"
          >
            Signup here
          </Link>
        </p>
      </div>

      {/* Admin Login Link */}
      <div className="mt-4 text-center">
        <p className="text-xs text-gray-500">
          Looking for admin access?{' '}
          <Link 
            to="/admin/login" 
            className="text-gray-600 hover:text-gray-800 hover:underline"
          >
            Admin Login
          </Link>
        </p>
      </div>
    </>
  );

  return (
    <>
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
        <Card className="w-full max-w-md shadow-xl">
          <CardHeader className="space-y-1">
            <div className="flex items-center justify-center mb-2">
              {branding?.logo_url ? (
                <img 
                  src={branding.logo_url} 
                  alt="Logo" 
                  className="w-16 h-16 object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div className={`p-3 bg-blue-100 rounded-full ${branding?.logo_url ? 'hidden' : ''}`}>
                <Store className="h-8 w-8 text-blue-600" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold text-center">
              {branding?.portal_title || 'Vendor Portal'}
            </CardTitle>
            <CardDescription className="text-center">
              Login to manage your store and products
            </CardDescription>
          </CardHeader>
          <CardContent>
            {showPassKey ? renderPassKeyForm() : renderLoginForm()}
          </CardContent>
        </Card>
      </div>

      {/* GST Reminder Toast Overlay */}
      {gstReminderToast && (
        <div className="fixed bottom-4 right-4 z-50 max-w-md">
          <Card className="border-2 border-amber-300 bg-amber-50 shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-amber-900 mb-1">
                    {gstReminderToast.message}
                  </p>
                  <div className="flex items-center gap-2 mt-3">
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-xs"
                      onClick={() => navigate('/vendor/tax-registration')}
                    >
                      Complete Now
                    </Button>
                    {gstReminderToast.showDismiss && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-xs"
                        onClick={handleDismissReminder}
                      >
                        Don&apos;t Remind Me
                      </Button>
                    )}
                    <button
                      onClick={() => setGstReminderToast(null)}
                      className="ml-auto text-gray-400 hover:text-gray-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  <p className="text-xs text-amber-700 mt-2">
                    Reminder {gstReminderToast.count} of 12
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
};

export default Login;
